import { Injectable, Optional, SkipSelf } from '@angular/core';
import { ConfigManagerService } from '../../shared/services/config-manager.service';
import {
    AnalyticsTag,
    ConfigEnvironments
} from '../../../../common/models/app-config.interface';

@Injectable()
export class AnalyticsService {
    private isGtagLoaded = false;
    private env: ConfigEnvironments;
    private readonly allowedGaIds = ['G-HD9LK91M6G', 'G-J73RGWEMGZ'];
    private pendingEvents: {
        eventName: string;
        params?: Record<string, any>;
    }[] = [];
    private readonly maxPendingEvents = 50;

    constructor(
        @Optional() @SkipSelf() existingInstance: AnalyticsService,
        private configManagerService: ConfigManagerService
    ) {
        if (existingInstance) {
            throw new Error(
                'AnalyticsService is already loaded. Import it in AppModule only.'
            );
        } else {
            this.loadGtag(this.configManagerService.environment);
        }
    }

    private loadGtag(env: ConfigEnvironments) {
        this.env = env;
        if (!this.shouldLoadGtag(env)) {
            return;
        }

        const gaId = this.getGoogleAnalyticsId(env);
        if (!this.isValidGoogleAnalyticsId(gaId)) {
            console.error('Invalid or unauthorized Google Analytics ID.');
            return;
        }

        const scriptUrl = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        this.appendScript(scriptUrl, () => {
            // Initialize gtag after script loads
            // Ensure dataLayer exists on the window
            window['dataLayer'] = window['dataLayer'] || [];

            // Define gtag as a standard function to preserve 'arguments'
            window['gtag'] = function () {
                window['dataLayer'].push(arguments);
            };

            // Now initialize GA
            window['gtag']('js', new Date());
            window['gtag']('config', gaId);

            this.isGtagLoaded = true;

            this.flushPendingEvents();
        });
    }

    private flushPendingEvents(): void {
        if (typeof window['gtag'] !== 'function') {
            return;
        }

        this.pendingEvents.forEach(({ eventName, params }) => {
            window['gtag']('event', eventName, params || {});
        });

        this.pendingEvents = [];
    }

    private shouldLoadGtag(env: ConfigEnvironments): boolean {
        return (
            (env === ConfigEnvironments.PDN ||
                env === ConfigEnvironments.PROD) &&
            typeof window !== 'undefined' &&
            !this.isGtagLoaded
        );
    }

    private getGoogleAnalyticsId(env: ConfigEnvironments): string {
        switch (env) {
            case ConfigEnvironments.PROD:
                return AnalyticsTag.PROD;
            case ConfigEnvironments.PDN:
                return AnalyticsTag.PDN;
            default:
                return '';
        }
    }

    private isValidGoogleAnalyticsId(gaId: string): boolean {
        return this.allowedGaIds.includes(gaId);
    }

    private appendScript(src: string, onLoad: () => void): void {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="${src}"]`
        );

        if (existingScript) {
            if (typeof window['gtag'] === 'function') {
                onLoad();
            } else {
                existingScript.addEventListener('load', onLoad, { once: true });
            }
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = src;
        script.crossOrigin = 'anonymous';
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener(
            'error',
            () => {
                console.error('Failed to load Google Analytics script.');
            },
            { once: true }
        );

        document.head.appendChild(script);
    }

    trackEvent(eventName: string, params?: Record<string, any>): void {
        if (!this.isGtagLoaded || typeof window['gtag'] !== 'function') {
            if (
                this.env !== ConfigEnvironments.DEV &&
                this.env !== ConfigEnvironments.QAI &&
                this.pendingEvents.length < this.maxPendingEvents
            ) {
                this.pendingEvents.push({
                    eventName,
                    params
                });
            }
            return;
        }

        window['gtag']('event', eventName, params || {});
    }
}
